import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ArrowUpRight, Sun, Moon } from "lucide-react";
import { SITE } from "@/data/site";
import { useLang } from "@/i18n/LanguageContext";
import { useTheme } from "@/theme/ThemeContext";

const ThemeToggle = ({ className = "" }) => {
  const { theme, toggle } = useTheme();
  return (
    <button
      onClick={toggle}
      data-testid="theme-toggle"
      aria-label={theme === "dark" ? "Passer en clair" : "Passer en sombre"}
      className={`inline-flex items-center justify-center w-9 h-9 border border-border rounded-full bg-card text-ink hover:border-ink transition-colors ${className}`}
    >
      {theme === "dark" ? <Sun size={15} /> : <Moon size={15} />}
    </button>
  );
};

const LangToggle = ({ className = "" }) => {
  const { lang, setLang } = useLang();
  const flags = { fr: "🇫🇷", en: "🇬🇧" };
  return (
    <div className={`inline-flex items-center border border-border rounded-full overflow-hidden bg-card ${className}`} data-testid="lang-toggle">
      {["fr", "en"].map((l) => (
        <button
          key={l}
          data-testid={`lang-${l}`}
          onClick={() => setLang(l)}
          aria-label={l === "fr" ? "Français" : "English"}
          className={`flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-bold uppercase tracking-wide transition-colors ${lang === l ? "bg-night text-white" : "text-ink hover:bg-secondary"}`}
        >
          <span className="text-sm leading-none">{flags[l]}</span>
          <span>{l}</span>
        </button>
      ))}
    </div>
  );
};

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();
  const { t } = useLang();

  const LINKS = [
    { to: "/", label: t.nav.home, id: "home" },
    { to: "/coaching-en-salle", label: t.nav.salle, id: "coaching-salle" },
    { to: "/coaching-a-domicile", label: t.nav.domicile, id: "coaching-domicile" },
    { to: "/coaching-en-ligne", label: t.nav.ligne, id: "coaching-ligne" },
    { to: "/a-propos", label: t.nav.about, id: "about" },
    { to: "/faq", label: t.nav.faq, id: "faq" },
    { to: "/blog", label: t.nav.blog, id: "blog" },
    { to: "/contact", label: t.nav.contact, id: "contact" },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [pathname]);

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${scrolled ? "glass border-b border-border" : "bg-transparent"}`}
    >
      <div className="max-w-8xl mx-auto px-6 lg:px-12 h-[70px] flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="font-display font-black text-lg tracking-tight uppercase leading-none">
          Gihed<span className="text-accentbrand">.</span>Coaching
        </Link>

        <nav className="hidden lg:flex items-center gap-6 xl:gap-8">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={`nav-link-${l.id}`}
              className={({ isActive }) =>
                `text-sm font-medium tracking-tight transition-colors hover:text-accentbrand whitespace-nowrap ${isActive ? "text-accentbrand" : "text-ink"}`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <ThemeToggle />
          <LangToggle />
          {(pathname === "/" || pathname === "/contact") && (
            <Link
              to="/contact"
              data-testid="nav-cta"
              className="hidden xl:inline-flex hover-arrow items-center gap-2 bg-accentbrand text-white text-sm font-bold uppercase tracking-wide px-5 py-3 hover:bg-[#E63E00] transition-colors"
            >
              {t.nav.cta} <ArrowUpRight size={16} />
            </Link>
          )}
          <button
            data-testid="nav-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden p-2 -mr-2 text-ink"
            aria-label="Menu"
          >
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      <div
        className={`lg:hidden overflow-hidden transition-all duration-500 ease-out glass ${open ? "max-h-[520px] border-b border-border" : "max-h-0"}`}
      >
        <nav className="px-6 py-6 flex flex-col gap-1">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={`mobile-nav-link-${l.id}`}
              className={({ isActive }) =>
                `py-3 border-b border-border font-display text-2xl font-bold uppercase tracking-tight ${isActive ? "text-accentbrand" : "text-ink"}`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <a
            href={SITE.whatsappLink}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="mobile-whatsapp-link"
            className="mt-4 inline-flex justify-center bg-accentbrand text-white font-bold uppercase tracking-wide text-sm px-5 py-4"
          >
            {t.common.whatsapp}
          </a>
        </nav>
      </div>

      {/* Mobile pill nav — horizontal scroll banner */}
      <div className="lg:hidden border-t border-border/60 glass" data-testid="mobile-pill-nav">
        <nav className="flex gap-2 overflow-x-auto no-scrollbar px-4 py-2.5">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={`pill-nav-${l.id}`}
              className={({ isActive }) =>
                `shrink-0 inline-flex items-center px-3.5 py-1.5 rounded-full text-[13px] font-bold uppercase tracking-tight whitespace-nowrap transition-colors ${
                  isActive
                    ? "bg-night text-white"
                    : "bg-card text-ink border border-border hover:border-ink"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>
      </div>
    </header>
  );
};
